// Libs
import classNames from 'classnames/bind';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { Navigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseTable, BaseToolbar, Loading, UnderDevelopment } from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
import { DetailGoogleGmailModal, FormSendGmail } from '~/components/module/googleGmail';
// Others
import { IGoogleGmail, IQueryGetListGoogleGmail } from '~/utils/interface/email';
import { IHeaderCellType } from '~/utils/interface/common';
import { extractNameAndEmail, formatDate } from '~/utils/helper/common';
import {
  DASH,
  DEFAULT_LIMIT_GOOGLE_GMAIL,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { DateFormatEnum, GoogleConnectEnum, RoleEnum } from '~/utils/enum/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getListGoogleGmail } from '~/thunks/google/googleThunk';
import { googleActions } from '~/thunks/google/googleSlice';
import { RootState } from '~/utils/redux/store';
import { StorageEnum } from '~/utils/enum/base';
import { adminRoute } from '~/utils/routes/admin';
import { staffRoute } from '~/utils/routes/staff';
// Styles, images, icons
import styles from './Gmail.module.scss';
import { icons } from '~/assets';

type Props = {};

const columns = (t: TFunction): IHeaderCellType<IGoogleGmail>[] => {
  return [
    {
      id: 'from',
      label: t('table_head_title_google_email_from'),
      render: (record: IGoogleGmail) => {
        return <>{record.from ? extractNameAndEmail(record.from).name : EMPTY_STRING}</>;
      },
      width: 250,
    },
    {
      id: 'subject',
      label: t('table_head_title_google_email_subject'),
      render: (record) => {
        return (
          <Box component={'div'} className={cx('content')}>
            <Box className={cx('subject')}>{`${record.subject} ${DASH}`}</Box>
            <Box className={cx('snippet')}>{record.snippet}</Box>
          </Box>
        );
      },
    },
    {
      id: 'createdAt',
      label: t('table_head_title_google_email_received_at'),
      render: (record: IGoogleGmail) =>
        record?.createdAt
          ? formatDate(new Date(record?.createdAt), DateFormatEnum.MM_DD_YYYY_HH_AM_PM)
          : EMPTY_STRING,
      width: 200,
    },
  ];
};

const cx = classNames.bind(styles);

const Gmail = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/googleEmail']);
  const dispatch = useAppDispatch();
  const currentRole = localStorage.getItem(StorageEnum.ROLE);
  //#endregion Declare Hook

  //#region Selector
  const { profile } = useAppSelector((state: RootState) => state.user);
  const { nextPageToken } = useAppSelector((state: RootState) => state.google);
  //#endregion Selector

  //#region Declare State
  const [listEmail, setListEmail] = useState<IGoogleGmail[]>([]);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [params, setParams] = useState<IQueryGetListGoogleGmail>({
    limit: DEFAULT_LIMIT_GOOGLE_GMAIL,
  });
  const [prevPageTokens, setPrevPageTokens] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowSendEmail, setIsShowSendEmail] = useState<boolean>(false);
  const [isShowGoogleGmailDetailModal, setIsShowGoogleGmailDetailModal] = useState<boolean>(false);
  const [dataDetail, setDataDetail] = useState<IGoogleGmail>({} as IGoogleGmail);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  const handleGetListGoogleGmail = useCallback(
    (params: IQueryGetListGoogleGmail) => {
      setIsLoading(true);

      dispatch(getListGoogleGmail(params))
        .unwrap()
        .then((res) => {
          const { responses, nextPageToken } = res.data;

          dispatch(googleActions.setNextPageToken(nextPageToken || EMPTY_STRING));

          setListEmail(responses);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  //#region Implement Hook
  useEffect(() => {
    setPrevPageTokens((prev) => (prev.length === DEFAULT_NUMBER_ZERO ? [EMPTY_STRING] : prev));

    handleGetListGoogleGmail(params);
  }, [params, handleGetListGoogleGmail]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleClickRow = (record: IGoogleGmail) => {
    setDataDetail(record);
    setIsShowGoogleGmailDetailModal(true);
  };

  const handleCloseModalDetail = () => {
    setDataDetail({} as IGoogleGmail);
    setIsShowGoogleGmailDetailModal(false);
  };

  const handleNextPage = () => {
    if (!nextPageToken) return;

    setPrevPageTokens((prev) => [...prev, nextPageToken]);

    setParams((prev) => {
      const newParams = { ...prev };
      newParams.nextPageToken = nextPageToken;
      return newParams;
    });
  };

  const handlePrevPage = () => {
    setPrevPageTokens((prevTokens) => {
      if (prevTokens.length <= DEFAULT_NUMBER_ONE) return prevTokens;

      const newTokens = [...prevTokens];
      newTokens.pop();

      const prevToken = newTokens[newTokens.length - DEFAULT_NUMBER_ONE];

      setParams((prev) => {
        const newParams = { ...prev };
        if (prevToken) {
          newParams.nextPageToken = prevToken;
        } else {
          delete newParams.nextPageToken;
        }
        return newParams;
      });

      return newTokens;
    });
  };

  const handleShowSendEmail = () => {
    setIsShowSendEmail(!isShowSendEmail);
  };

  const navigateBase = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        return <Navigate to={adminRoute.base} />;

      case RoleEnum.STAFF:
        return <Navigate to={staffRoute.base} />;

      default:
        break;
    }
  };
  //#endregion Handle Function

  return profile.google?.status && profile.google?.status !== GoogleConnectEnum.CONNECTED ? (
    navigateBase()
  ) : (
    <PageShell title={t('tile_quantity_google_email_page')}>
    <Box component={'div'} id='gmailPage' className={cx('container')}>
      <BaseToolbar>
        <Button variant='contained' className={cx('btn')} onClick={handleShowSendEmail}>
          {t('btn_send_email_label')}
        </Button>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          columns={columns(t)}
          dataSource={listEmail || []}
          onRowClick={(_, record) => handleClickRow(record)}
        />

        <Box component={'div'} className={cx('pagination')}>
          {listEmail?.length > DEFAULT_NUMBER_ZERO && (
            <Box>
              <Button
                variant='text'
                startIcon={
                  <img
                    className={cx('prevIcon')}
                    src={
                      prevPageTokens.length === DEFAULT_NUMBER_ONE
                        ? icons.paginationIconPreviousDisabled
                        : icons.paginationIconPreviousActive
                    }
                    alt={t('alt_img')}
                  />
                }
                className={cx('btnNext')}
                onClick={handlePrevPage}
                disabled={prevPageTokens.length === DEFAULT_NUMBER_ONE}
              >
                <Typography component={'span'} className={cx('nextContent')}>
                  {t('pagination_previous_title')}
                </Typography>
              </Button>

              <Button
                variant='text'
                endIcon={
                  <img
                    className={cx('nextIcon')}
                    src={nextPageToken ? icons.paginationIconNextActive : icons.paginationIconNextDisabled}
                    alt={t('alt_img')}
                  />
                }
                className={cx('btnNext')}
                onClick={handleNextPage}
                disabled={!nextPageToken}
              >
                <Typography component={'span'} className={cx('nextContent')}>
                  {t('pagination_next_title')}
                </Typography>
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {isLoading && <Loading />}

      {isShowSendEmail && <FormSendGmail onClose={handleShowSendEmail} />}

      {isShowGoogleGmailDetailModal && (
        <DetailGoogleGmailModal
          data={dataDetail}
          isOpen={isShowGoogleGmailDetailModal}
          onClose={handleCloseModalDetail}
        />
      )}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
    </PageShell>
  );
};

export default Gmail;
